export type ShapeKind = "rect" | "ellipse" | "triangle";
export type ToolMode =
  | "select"
  | "pen"
  | "shape"
  | "polygon"
  | "erase"
  | "restore";
export type MaskStyle = "mosaic" | "blur" | "fill";
export type ExportFormat = "original" | "png" | "jpeg" | "webp";

/**
 * Where an image's effect lands.
 * - inside: each drawn range applies its own effect
 * - outside: everything except the drawn ranges gets the image effect
 * - all: the whole image gets the image effect, ranges apply on top
 */
export type Coverage = "inside" | "outside" | "all";

export interface Point {
  x: number;
  y: number;
}

export interface RegionEffect {
  maskStyle: MaskStyle;
  fillColor: string;
  blockSize: number;
}

/**
 * Ranges carry no scale: resizing is baked into their geometry so a resized
 * pen stroke keeps a round brush instead of being squashed into an ellipse.
 */
interface RegionBase {
  id: string;
  x: number;
  y: number;
  rotation: number;
  effect: RegionEffect;
}

export interface ShapeRegion extends RegionBase {
  kind: ShapeKind;
  width: number;
  height: number;
}

/** Points are relative to (x, y). */
export interface StrokeRegion extends RegionBase {
  kind: "stroke";
  points: Point[];
  penWidth: number;
}

/** Closed outline; points are relative to (x, y). */
export interface PolygonRegion extends RegionBase {
  kind: "polygon";
  points: Point[];
}

/** Eraser strokes use absolute image coordinates. */
export interface EraseRegion {
  id: string;
  kind: "erase";
  points: Point[];
  penWidth: number;
}

export type EditableRegion = ShapeRegion | StrokeRegion | PolygonRegion;
export type MosaicRegion = EditableRegion | EraseRegion;

export function isShapeRegion(region: MosaicRegion): region is ShapeRegion {
  return (
    region.kind === "rect" ||
    region.kind === "ellipse" ||
    region.kind === "triangle"
  );
}

export interface RestorationPatch {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  dataUrl: string;
  strength: number;
}

export interface EditorImage {
  id: string;
  name: string;
  mime: string;
  width: number;
  height: number;
  sourceUrl: string;
  thumbnailUrl: string;
  originalBytes: ArrayBuffer;
  coverage: Coverage;
  coverageEffect: RegionEffect;
  regions: MosaicRegion[];
  restorations: RestorationPatch[];
  dirty: boolean;
  saved: boolean;
  relativePath?: string;
}

export interface EditorSettings {
  tool: ToolMode;
  shapeKind: ShapeKind;
  /** Effect given to newly drawn ranges. */
  effect: RegionEffect;
  penWidth: number;
  showOutlines: boolean;
  showGrid: boolean;
  preview: boolean;
  /** Download the current image when moving to another one. */
  autoDownload: boolean;
  stripMetadata: boolean;
  exportFormat: ExportFormat;
  exportScale: number;
  exportQuality: number;
  exportRemoveBackground: boolean;
}

/** Solid-fill swatches offered for the "fill" mask style. */
export const FILL_COLORS = [
  "#000000",
  "#ffffff",
  "#8b9391",
  "#e8c9a8",
  "#ff654f",
] as const;

export const BLOCK_SIZE_RANGE = { min: 4, max: 200 } as const;
export const PEN_WIDTH_RANGE = { min: 4, max: 300 } as const;

export interface MosaicJob {
  requestId: number;
  imageData: ImageData;
  regions: MosaicRegion[];
  coverage: Coverage;
  coverageEffect: RegionEffect;
}

export interface MosaicJobResult {
  requestId: number;
  imageData?: ImageData;
  error?: string;
}

export interface RestorationJob {
  requestId: number;
  imageData: ImageData;
  strength: number;
}

export interface RestorationJobResult {
  requestId: number;
  imageData?: ImageData;
  provider?: "webgpu" | "wasm";
  error?: string;
}

export interface BackgroundRemovalJob {
  requestId: number;
  imageData: ImageData;
}

export interface BackgroundRemovalJobResult {
  requestId: number;
  imageData?: ImageData;
  error?: string;
}

export const DEFAULT_EFFECT: RegionEffect = {
  maskStyle: "mosaic",
  fillColor: FILL_COLORS[0],
  blockSize: 20,
};

export const DEFAULT_SETTINGS: EditorSettings = {
  tool: "select",
  shapeKind: "rect",
  effect: DEFAULT_EFFECT,
  penWidth: 40,
  showOutlines: true,
  showGrid: false,
  preview: false,
  autoDownload: false,
  // Anonymising a photo and shipping its GPS tag is a footgun, so stripping
  // is the default and preserving metadata is the deliberate choice.
  stripMetadata: true,
  exportFormat: "original",
  exportScale: 1,
  exportQuality: 92,
  exportRemoveBackground: false,
};
